import { Pub, State, Sub } from '../../../../../shared/state/state.abstract.ts';
import { ITabState, tabStateModel } from '../../../states/tabState.model.ts';
import {
  CustomerCashAssistDataRes,
  customerService,
  SaveCustomerDataRes,
} from '../../../services/customer.service.ts';
import { filter, finalize, map, merge, Observable, switchMap, take, tap } from 'rxjs';
import { customersListState } from '../../../states/customersList.state.ts';
import { customerTabLoadingService } from '../customerTabLoading.service.ts';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler.ts';
import {
  C_Save_Operation_Status,
  SaveCustomerDataMutationVariables,
} from '../../../../../graphql/generatedModel.ts';
import { defaultCustomerCashAssistData } from '../../../loaders/cashAssistTab.resolver.ts';

class PubImpl extends Pub<ICashAssistTabState> {
  save(dataToSave: SaveCustomerDataMutationVariables) {
    this.emit('save', { dataToSave });
  }
  clearStream() {
    this.emit(undefined, {});
  }
}

class SubImpl extends Sub<ICashAssistTabState> {
  protected actionHandlers(): Observable<ICashAssistTabState> {
    return merge(this.selectedCustomer(), this.save()).pipe();
  }
  private save(): Observable<ICashAssistTabState> {
    return this.actionListener('save').pipe(
      tap(({ dataToSave }) => {
        if (dataToSave) {
          customerService.sub
            .editCustomerData()
            .pipe(
              responseHandler<SaveCustomerDataRes | undefined>({
                success: () => 'customer.customer_saved',
                customErrorHandler: () => 'common.error_chnages_not_saved',
              }),
              filter((v) => v !== undefined),
              take(1),
            )
            .subscribe((res) => {
              if (res?.status === C_Save_Operation_Status.SOS1_DATA_CHANGED) {
                const { updatedGridItem } = res;
                customersListState.pub.updateCustomer(updatedGridItem!);
              }
            });
          customerService.pub.editCustomerData(dataToSave);
        }
      }),
      finalize(() => cashAssistTabState.pub.clearStream()),
    );
  }
  private selectedCustomer(): Observable<ICashAssistTabState> {
    return customersListState.sub.state().pipe(
      filter(({ action }) => action === 'selectRecord'),
      filter(({ selectedRecord }) => typeof selectedRecord?.id === 'string'),
      switchMap(({ selectedRecord }) => {
        customerTabLoadingService.pub.loading(true);
        const id = selectedRecord!.id;
        const state = this.stream$.getValue();
        state.action = 'list.selectedRecord';

        const details = customerService.sub.customerCashAssistData().pipe(
          responseHandler<CustomerCashAssistDataRes>({
            errorReturnType: defaultCustomerCashAssistData,
          }),
          map((data) => {
            state.defaultValues = data;
            customersListState.pub.updateInfoTip(data.informationTip);
            customerTabLoadingService.pub.loading(false);
            return state;
          }),
        );
        customerService.pub.customerCashAssistData({ id });
        return details;
      }),
    );
  }
}

class CashAssistTabState extends State<ICashAssistTabState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const cashAssistTabState = new CashAssistTabState({
  ...tabStateModel,
  action: undefined,
  dataToSave: undefined,
});
export interface ICashAssistTabState extends Pick<ITabState, 'defaultValues'> {
  action: undefined | 'list.selectedRecord' | 'save';
  dataToSave: SaveCustomerDataMutationVariables | undefined;
}
